import React, { useEffect, useState } from 'react';
import {
  CheckCircle,
  XCircle,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Loader2,
  Plus,
  RefreshCw,
  Trash2,
  X,
} from 'lucide-react';
import api from '@/utils/api';

interface IntegrationConfig {
  url?: string;
  username?: string;
  password?: string;
  api_key?: string;
  client_id?: string;
  client_secret?: string;
  redirect_uri?: string;
  [key: string]: string | undefined;
}

interface Integration {
  integration_type: string;
  name: string;
  description: string;
  auth_type: 'basic' | 'apikey' | 'oauth2' | 'oauth2_client_credentials';
  url_placeholder?: string;
  is_enabled: boolean;
  connection_status: 'connected' | 'disconnected' | 'error' | 'unknown';
  config: IntegrationConfig;
  last_tested_at?: string;
  is_custom?: boolean;
}

const STATUS_STYLES: Record<string, { label: string; classes: string; icon: React.ReactNode }> = {
  connected: {
    label: 'Connected',
    classes: 'bg-green-100 text-green-800',
    icon: <CheckCircle className="w-3.5 h-3.5 text-green-600" />,
  },
  disconnected: {
    label: 'Not connected',
    classes: 'bg-gray-100 text-gray-600',
    icon: <XCircle className="w-3.5 h-3.5 text-gray-400" />,
  },
  error: {
    label: 'Error',
    classes: 'bg-red-100 text-red-800',
    icon: <XCircle className="w-3.5 h-3.5 text-red-600" />,
  },
  unknown: {
    label: 'Unknown',
    classes: 'bg-yellow-100 text-yellow-700',
    icon: <AlertCircle className="w-3.5 h-3.5 text-yellow-500" />,
  },
};

const INTEGRATION_ICONS: Record<string, string> = {
  servicenow: '🔧',
  salesforce: '☁️',
  sap: '🏭',
  workday: '👥',
  halo: '🎯',
  gmail: '📧',
  hcmpro: '👤',
};

const INPUT = "w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-fuchsia-500 bg-white";
const BTN_PRIMARY = "inline-flex items-center gap-1.5 px-4 py-2 bg-fuchsia-600 text-white text-sm font-medium rounded-md hover:bg-fuchsia-700 disabled:opacity-50";
const BTN_OUTLINE = "inline-flex items-center gap-1.5 px-4 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50 disabled:opacity-50";
const BTN_SECONDARY = "inline-flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700";

// ── Add Integration Modal ─────────────────────────────────────────────────────

interface NewIntegration {
  name: string;
  integration_type: string;
  description: string;
  auth_type: 'basic' | 'apikey' | 'oauth2';
  url: string;
  username: string;
  password: string;
  api_key: string;
  client_id: string;
  client_secret: string;
}

function slugify(name: string) {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
}

interface AddModalProps {
  onClose: () => void;
  onCreated: () => void;
}

const AddIntegrationModal: React.FC<AddModalProps> = ({ onClose, onCreated }) => {
  const [form, setForm] = useState<NewIntegration>({
    name: '',
    integration_type: '',
    description: '',
    auth_type: 'basic',
    url: '',
    username: '',
    password: '',
    api_key: '',
    client_id: '',
    client_secret: '',
  });
  const [slugEdited, setSlugEdited] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const set = (field: keyof NewIntegration, value: string) => {
    setForm(prev => {
      const next = { ...prev, [field]: value };
      if (field === 'name' && !slugEdited) {
        next.integration_type = slugify(value);
      }
      return next;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || !form.integration_type.trim()) {
      setError('Name and identifier are required');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const config: IntegrationConfig = {};
      if (form.url) config.url = form.url;
      if (form.auth_type === 'basic') {
        if (form.username) config.username = form.username;
        if (form.password) config.password = form.password;
      } else if (form.auth_type === 'apikey') {
        if (form.api_key) config.api_key = form.api_key;
      } else {
        if (form.client_id) config.client_id = form.client_id;
        if (form.client_secret) config.client_secret = form.client_secret;
      }
      await api.post('/integrations', {
        integration_type: form.integration_type,
        name: form.name,
        description: form.description,
        auth_type: form.auth_type,
        url_placeholder: form.url || undefined,
        config,
      });
      onCreated();
    } catch (err: any) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Add Integration</h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4 max-h-[80vh] overflow-y-auto">
          {/* Name + slug */}
          <Field label="Integration name" required>
            <input
              type="text"
              value={form.name}
              onChange={e => set('name', e.target.value)}
              placeholder="e.g. Jira Cloud"
              className={INPUT}
              autoFocus
            />
          </Field>

          <Field label="Identifier (slug)" required>
            <input
              type="text"
              value={form.integration_type}
              onChange={e => { setSlugEdited(true); set('integration_type', e.target.value); }}
              placeholder="jira_cloud"
              className={INPUT}
            />
            <p className="text-xs text-gray-400 mt-1">Lowercase letters, numbers and underscores only</p>
          </Field>

          <Field label="Description">
            <input
              type="text"
              value={form.description}
              onChange={e => set('description', e.target.value)}
              placeholder="Short description of this integration"
              className={INPUT}
            />
          </Field>

          {/* Auth type */}
          <Field label="Authentication type" required>
            <select
              value={form.auth_type}
              onChange={e => set('auth_type', e.target.value)}
              className={INPUT}
            >
              <option value="basic">Basic Auth (URL + username + password)</option>
              <option value="apikey">API Key</option>
              <option value="oauth2">OAuth 2.0 (client credentials)</option>
            </select>
          </Field>

          <div className="border-t border-gray-100 pt-3 space-y-3">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Credentials</p>

            {/* URL — shown for basic & apikey */}
            {(form.auth_type === 'basic' || form.auth_type === 'apikey') && (
              <Field label="Instance URL">
                <input
                  type="url"
                  value={form.url}
                  onChange={e => set('url', e.target.value)}
                  placeholder="https://your-instance.example.com"
                  className={INPUT}
                />
              </Field>
            )}

            {form.auth_type === 'basic' && (
              <div className="grid grid-cols-2 gap-3">
                <Field label="Username">
                  <input
                    type="text"
                    value={form.username}
                    onChange={e => set('username', e.target.value)}
                    placeholder="admin"
                    className={INPUT}
                    autoComplete="username"
                  />
                </Field>
                <Field label="Password">
                  <input
                    type="password"
                    value={form.password}
                    onChange={e => set('password', e.target.value)}
                    placeholder="••••••••"
                    className={INPUT}
                    autoComplete="new-password"
                  />
                </Field>
              </div>
            )}

            {form.auth_type === 'apikey' && (
              <Field label="API Key">
                <input
                  type="password"
                  value={form.api_key}
                  onChange={e => set('api_key', e.target.value)}
                  placeholder="your-api-key"
                  className={INPUT}
                  autoComplete="new-password"
                />
              </Field>
            )}

            {form.auth_type === 'oauth2' && (
              <>
                <Field label="Client ID">
                  <input
                    type="text"
                    value={form.client_id}
                    onChange={e => set('client_id', e.target.value)}
                    placeholder="client id"
                    className={INPUT}
                  />
                </Field>
                <Field label="Client Secret">
                  <input
                    type="password"
                    value={form.client_secret}
                    onChange={e => set('client_secret', e.target.value)}
                    placeholder="••••••••"
                    className={INPUT}
                    autoComplete="new-password"
                  />
                </Field>
              </>
            )}
          </div>

          {error && (
            <div className="px-3 py-2 bg-red-50 border border-red-100 rounded-md text-sm text-red-700 flex items-center gap-2">
              <XCircle className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          )}

          <div className="flex gap-2 pt-2">
            <button type="submit" disabled={saving} className={BTN_PRIMARY}>
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
              Add Integration
            </button>
            <button type="button" onClick={onClose} className={BTN_OUTLINE}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── Main component ────────────────────────────────────────────────────────────

export const IntegrationsSettings: React.FC = () => {
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [forms, setForms] = useState<Record<string, IntegrationConfig>>({});
  const [saving, setSaving] = useState<string | null>(null);
  const [testing, setTesting] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<Record<string, { status: string; message: string }>>({});
  const [saveMessage, setSaveMessage] = useState<Record<string, string>>({});

  useEffect(() => {
    fetchIntegrations();
    const params = new URLSearchParams(window.location.search);
    if (params.get('gmail') === 'connected') {
      setTestResult(prev => ({ ...prev, gmail: { status: 'connected', message: 'Gmail connected successfully!' } }));
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const fetchIntegrations = async () => {
    setLoading(true);
    try {
      const res = await api.get('/integrations');
      const data: Integration[] = res.data;
      setIntegrations(data);
      const initial: Record<string, IntegrationConfig> = {};
      data.forEach(i => { initial[i.integration_type] = { ...i.config }; });
      setForms(initial);
    } catch (err) {
      console.error('Failed to fetch integrations', err);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleExpand = (type: string) => {
    setExpanded(prev => (prev === type ? null : type));
    setTestResult(prev => { const n = { ...prev }; delete n[type]; return n; });
    setSaveMessage(prev => { const n = { ...prev }; delete n[type]; return n; });
  };

  const handleFieldChange = (type: string, field: keyof IntegrationConfig, value: string) => {
    setForms(prev => ({ ...prev, [type]: { ...prev[type], [field]: value } }));
  };

  const handleSave = async (type: string) => {
    setSaving(type);
    setSaveMessage(prev => { const n = { ...prev }; delete n[type]; return n; });
    try {
      await api.put(`/integrations/${type}`, { config: forms[type] });
      setSaveMessage(prev => ({ ...prev, [type]: 'Configuration saved successfully' }));
      await fetchIntegrations();
    } catch (err: any) {
      setSaveMessage(prev => ({ ...prev, [type]: `Save failed: ${err?.response?.data?.detail || err.message}` }));
    } finally {
      setSaving(null);
    }
  };

  const handleTest = async (type: string) => {
    setTesting(type);
    setTestResult(prev => { const n = { ...prev }; delete n[type]; return n; });
    try {
      const res = await api.post(`/integrations/${type}/test`);
      setTestResult(prev => ({ ...prev, [type]: { status: res.data.status, message: res.data.message } }));
      await fetchIntegrations();
    } catch (err: any) {
      const msg = err?.response?.data?.detail || err.message;
      setTestResult(prev => ({ ...prev, [type]: { status: 'error', message: msg } }));
    } finally {
      setTesting(null);
    }
  };

  const handleDisconnect = async (type: string) => {
    if (!confirm(`Remove ${type} integration? This will delete all saved credentials.`)) return;
    try {
      await api.delete(`/integrations/${type}`);
      await fetchIntegrations();
      setExpanded(null);
    } catch (err: any) {
      console.error('Disconnect failed', err);
    }
  };

  const handleGmailAuthorize = async () => {
    try {
      const res = await api.get('/integrations/gmail/oauth/authorize');
      window.open(res.data.auth_url, '_blank');
    } catch (err: any) {
      const msg = err?.response?.data?.detail || err.message;
      setTestResult(prev => ({ ...prev, gmail: { status: 'error', message: msg } }));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-fuchsia-600 mr-2" />
        <span className="text-gray-500">Loading integrations…</span>
      </div>
    );
  }

  return (
    <>
      {showAddModal && (
        <AddIntegrationModal
          onClose={() => setShowAddModal(false)}
          onCreated={() => { setShowAddModal(false); fetchIntegrations(); }}
        />
      )}

      <div className="p-6 max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Integrations</h2>
            <p className="text-sm text-gray-500 mt-1">Connect Fuchsia to your enterprise systems</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={fetchIntegrations} className="p-2 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100">
              <RefreshCw className="w-4 h-4" />
            </button>
            <button
              onClick={() => setShowAddModal(true)}
              className={BTN_PRIMARY}
            >
              <Plus className="w-4 h-4" />
              Add Integration
            </button>
          </div>
        </div>

        <div className="space-y-3">
          {integrations.map(integration => {
            const isOpen = expanded === integration.integration_type;
            const statusInfo = STATUS_STYLES[integration.connection_status] ?? STATUS_STYLES.unknown;
            const form = forms[integration.integration_type] ?? {};
            const isSaving = saving === integration.integration_type;
            const isTesting = testing === integration.integration_type;
            const result = testResult[integration.integration_type];
            const savedMsg = saveMessage[integration.integration_type];
            const isGmail = integration.auth_type === 'oauth2' && integration.integration_type === 'gmail';
            const icon = INTEGRATION_ICONS[integration.integration_type] ?? '🔌';

            return (
              <div
                key={integration.integration_type}
                className="bg-white rounded-lg border border-gray-200 overflow-hidden"
              >
                {/* Row header */}
                <div
                  className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-gray-50"
                  onClick={() => handleToggleExpand(integration.integration_type)}
                >
                  <div className="flex items-center space-x-3">
                    <span className="text-2xl">{icon}</span>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-gray-900 text-sm">{integration.name}</h3>
                        {integration.is_custom && (
                          <span className="px-1.5 py-0.5 text-xs bg-fuchsia-50 text-fuchsia-600 rounded font-medium">Custom</span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500">{integration.description}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${statusInfo.classes}`}>
                      {statusInfo.icon}
                      {statusInfo.label}
                    </span>
                    {isOpen ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                  </div>
                </div>

                {/* Expanded config form */}
                {isOpen && (
                  <div className="border-t border-gray-100 px-5 py-5 bg-gray-50">
                    {isGmail ? (
                      <GmailForm
                        form={form}
                        integration={integration}
                        onChange={(field, val) => handleFieldChange(integration.integration_type, field, val)}
                        onSave={() => handleSave(integration.integration_type)}
                        onTest={() => handleTest(integration.integration_type)}
                        onAuthorize={handleGmailAuthorize}
                        isSaving={isSaving}
                        isTesting={isTesting}
                      />
                    ) : integration.auth_type === 'oauth2_client_credentials' ? (
                      <ClientCredentialsForm
                        form={form}
                        integration={integration}
                        onChange={(field, val) => handleFieldChange(integration.integration_type, field, val)}
                        onSave={() => handleSave(integration.integration_type)}
                        onTest={() => handleTest(integration.integration_type)}
                        isSaving={isSaving}
                        isTesting={isTesting}
                      />
                    ) : integration.auth_type === 'apikey' ? (
                      <ApiKeyForm
                        form={form}
                        integration={integration}
                        onChange={(field, val) => handleFieldChange(integration.integration_type, field, val)}
                        onSave={() => handleSave(integration.integration_type)}
                        onTest={() => handleTest(integration.integration_type)}
                        isSaving={isSaving}
                        isTesting={isTesting}
                      />
                    ) : (
                      <BasicAuthForm
                        form={form}
                        integration={integration}
                        onChange={(field, val) => handleFieldChange(integration.integration_type, field, val)}
                        onSave={() => handleSave(integration.integration_type)}
                        onTest={() => handleTest(integration.integration_type)}
                        isSaving={isSaving}
                        isTesting={isTesting}
                      />
                    )}

                    {result && (
                      <div className={`mt-3 px-3 py-2 rounded-md text-sm flex items-center gap-2 ${
                        result.status === 'connected' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
                      }`}>
                        {result.status === 'connected'
                          ? <CheckCircle className="w-4 h-4 flex-shrink-0" />
                          : <XCircle className="w-4 h-4 flex-shrink-0" />}
                        {result.message}
                      </div>
                    )}

                    {savedMsg && (
                      <div className={`mt-3 px-3 py-2 rounded-md text-sm ${
                        savedMsg.startsWith('Save failed') ? 'bg-red-50 text-red-800' : 'bg-green-50 text-green-800'
                      }`}>
                        {savedMsg}
                      </div>
                    )}

                    {(integration.is_enabled || integration.is_custom) && (
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <button
                          onClick={() => handleDisconnect(integration.integration_type)}
                          className="flex items-center gap-1.5 text-xs text-red-500 hover:text-red-700"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                          {integration.is_custom ? 'Remove integration' : 'Remove credentials'}
                        </button>
                      </div>
                    )}

                    {integration.last_tested_at && (
                      <p className="mt-3 text-xs text-gray-400">
                        Last tested: {new Date(integration.last_tested_at).toLocaleString()}
                      </p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
};

// ── Sub-forms ──────────────────────────────────────────────────────────────────

interface BaseFormProps {
  form: IntegrationConfig;
  integration: Integration;
  onChange: (field: keyof IntegrationConfig, value: string) => void;
  onSave: () => void;
  onTest: () => void;
  isSaving: boolean;
  isTesting: boolean;
}

const BasicAuthForm: React.FC<BaseFormProps> = ({ form, integration, onChange, onSave, onTest, isSaving, isTesting }) => (
  <div className="space-y-3">
    <Field label="Instance URL" required>
      <input
        type="url"
        value={form.url ?? ''}
        onChange={e => onChange('url', e.target.value)}
        placeholder={integration.url_placeholder ?? 'https://'}
        className={INPUT}
      />
    </Field>
    <div className="grid grid-cols-2 gap-3">
      <Field label="Username" required>
        <input
          type="text"
          value={form.username ?? ''}
          onChange={e => onChange('username', e.target.value)}
          placeholder="admin"
          className={INPUT}
          autoComplete="username"
        />
      </Field>
      <Field label="Password" required>
        <input
          type="password"
          value={form.password ?? ''}
          onChange={e => onChange('password', e.target.value)}
          placeholder="••••••••"
          className={INPUT}
          autoComplete="new-password"
        />
      </Field>
    </div>
    <FormActions onSave={onSave} onTest={onTest} isSaving={isSaving} isTesting={isTesting} canTest={integration.is_enabled} />
  </div>
);

const ApiKeyForm: React.FC<BaseFormProps> = ({ form, integration, onChange, onSave, onTest, isSaving, isTesting }) => (
  <div className="space-y-3">
    <Field label="Instance URL">
      <input
        type="url"
        value={form.url ?? ''}
        onChange={e => onChange('url', e.target.value)}
        placeholder={integration.url_placeholder ?? 'https://'}
        className={INPUT}
      />
    </Field>
    <Field label="API Key" required>
      <input
        type="password"
        value={form.api_key ?? ''}
        onChange={e => onChange('api_key', e.target.value)}
        placeholder="your-api-key"
        className={INPUT}
        autoComplete="new-password"
      />
    </Field>
    <FormActions onSave={onSave} onTest={onTest} isSaving={isSaving} isTesting={isTesting} canTest={integration.is_enabled} />
  </div>
);

const ClientCredentialsForm: React.FC<BaseFormProps> = ({ form, integration, onChange, onSave, onTest, isSaving, isTesting }) => (
  <div className="space-y-3">
    <Field label="Instance URL" required>
      <input
        type="url"
        value={form.url ?? ''}
        onChange={e => onChange('url', e.target.value)}
        placeholder={integration.url_placeholder ?? 'https://'}
        className={INPUT}
      />
    </Field>
    <Field label="Client ID" required>
      <input
        type="text"
        value={form.client_id ?? ''}
        onChange={e => onChange('client_id', e.target.value)}
        placeholder="your-client-id"
        className={INPUT}
        autoComplete="username"
      />
    </Field>
    <Field label="Client Secret" required>
      <input
        type="password"
        value={form.client_secret ?? ''}
        onChange={e => onChange('client_secret', e.target.value)}
        placeholder="••••••••"
        className={INPUT}
        autoComplete="new-password"
      />
    </Field>
    <FormActions onSave={onSave} onTest={onTest} isSaving={isSaving} isTesting={isTesting} canTest={integration.is_enabled} />
  </div>
);

interface GmailFormProps extends BaseFormProps {
  onAuthorize: () => void;
}

const DEFAULT_REDIRECT_URI = `${window.location.origin}/oauth/gmail/callback`;

const GmailForm: React.FC<GmailFormProps> = ({ form, integration, onChange, onSave, onTest, onAuthorize, isSaving, isTesting }) => (
  <div className="space-y-3">
    <div className="bg-blue-50 border border-blue-100 rounded-md px-4 py-3 text-xs text-blue-800 space-y-1">
      <p className="font-semibold">Setup instructions</p>
      <ol className="list-decimal list-inside space-y-0.5">
        <li>Go to <a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noreferrer" className="underline inline-flex items-center gap-0.5">Google Cloud Console <ExternalLink className="w-3 h-3" /></a></li>
        <li>Create a project, enable the Gmail API</li>
        <li>Create OAuth 2.0 credentials → <strong>Web application</strong></li>
        <li>Add the Authorised Redirect URI shown below</li>
        <li>Enter the Client ID and Client Secret below, then save</li>
        <li>Click <strong>Authorize Gmail</strong> to complete the OAuth flow</li>
      </ol>
    </div>
    <Field label="Client ID" required>
      <input
        type="text"
        value={form.client_id ?? ''}
        onChange={e => onChange('client_id', e.target.value)}
        placeholder="123456789-abcdef.apps.googleusercontent.com"
        className={INPUT}
      />
    </Field>
    <Field label="Client Secret" required>
      <input
        type="password"
        value={form.client_secret ?? ''}
        onChange={e => onChange('client_secret', e.target.value)}
        placeholder="GOCSPX-••••••••"
        className={INPUT}
        autoComplete="new-password"
      />
    </Field>
    <Field label="Authorised Redirect URI">
      <input
        type="url"
        value={form.redirect_uri || DEFAULT_REDIRECT_URI}
        onChange={e => onChange('redirect_uri', e.target.value)}
        className={INPUT}
      />
      <p className="text-xs text-gray-400 mt-1">Copy this URL into Google Cloud Console → OAuth 2.0 credentials → Authorised redirect URIs</p>
    </Field>
    <div className="flex flex-wrap gap-2 pt-1">
      <button onClick={onSave} disabled={isSaving} className={BTN_PRIMARY}>
        {isSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
        Save credentials
      </button>
      {integration.is_enabled && (
        <>
          <button onClick={onAuthorize} className={BTN_SECONDARY}>
            <ExternalLink className="w-3.5 h-3.5" />
            Authorize Gmail
          </button>
          <button onClick={onTest} disabled={isTesting} className={BTN_OUTLINE}>
            {isTesting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
            Check status
          </button>
        </>
      )}
    </div>
  </div>
);

const FormActions: React.FC<{ onSave: () => void; onTest: () => void; isSaving: boolean; isTesting: boolean; canTest: boolean }> = ({
  onSave, onTest, isSaving, isTesting, canTest
}) => (
  <div className="flex gap-2 pt-1">
    <button onClick={onSave} disabled={isSaving} className={BTN_PRIMARY}>
      {isSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
      Save
    </button>
    {canTest && (
      <button onClick={onTest} disabled={isTesting} className={BTN_OUTLINE}>
        {isTesting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
        Test connection
      </button>
    )}
  </div>
);

const Field: React.FC<{ label: string; required?: boolean; children: React.ReactNode }> = ({ label, required, children }) => (
  <div>
    <label className="block text-xs font-medium text-gray-700 mb-1">
      {label}{required && <span className="text-red-400 ml-0.5">*</span>}
    </label>
    {children}
  </div>
);
